import { Dispatch } from "redux";
// @ts-ignore
import instance from "../../api/instance";
import {
  setSubscriptionDetails,
  setSubscriptionError,
  setSubscriptionLoading,
  setSubscriptions,
} from "../reducers/subscriptionReducer";
import { SubscriptionGroup } from "../types/subscriptionTypes";

//@ts-ignore
import {
  subscriptionConfig,
  createCheckoutSession,
  topupPurchase,
  razorpayCreateCheckoutSession,
  razorpayOrderComplete,
  subscriptionDetailsRoute,
} from "../../api/apiRoutes";

interface CheckoutSessionResponse {
  session_url: string;
}

const checkoutAuthHeaders = (token: string) => ({
  headers: { Authorization: `Token ${token}` },
});

/**
 * Extract a user-facing message from an axios error.
 * Handles plain `{ message }` / `{ detail }` shapes AND DRF field-keyed arrays
 * like `{ credits: ["For INR payments, the maximum is 8474 credits ..."] }`.
 */
const extractApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.detail === "string") return data.detail;
  if (data && typeof data === "object") {
    for (const key of Object.keys(data)) {
      const v = (data as Record<string, unknown>)[key];
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
      if (typeof v === "string") return v;
    }
  }
  return error?.message || fallback;
};

const redirectToStripeCheckout = async (
  dispatch: Dispatch,
  request: () => Promise<{ data: CheckoutSessionResponse }>,
) => {
  try {
    dispatch(setSubscriptionLoading(true));
    const response = await request();
    const sessionUrl = response?.data?.session_url;
    if (!sessionUrl || typeof sessionUrl !== "string") {
      throw new Error("Invalid checkout session response");
    }
    const allowedOrigins = [
      "https://checkout.stripe.com",
      "https://api.razorpay.com",
    ];
    try {
      const origin = new URL(sessionUrl).origin;
      if (!allowedOrigins.includes(origin)) {
        throw new Error(`Blocked redirect to untrusted origin: ${origin}`);
      }
    } catch {
      throw new Error("Invalid checkout URL");
    }
    window.location.href = sessionUrl;
    return response;
  } catch (error: any) {
    dispatch(
      setSubscriptionError(
        error.response?.data?.message ||
          error.message ||
          "Failed to create checkout session",
      ),
    );
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

export const loadSubscriptions =
  () => async (dispatch: Dispatch, getState: Function) => {
    const subscriptions = getState().subscriptions;

    if (
      subscriptions &&
      subscriptions.razorpay &&
      subscriptions.stripe &&
      (subscriptions.razorpay.free?.length > 0 ||
        subscriptions.stripe.free?.length > 0)
    ) {
      return;
    }

    try {
      dispatch(setSubscriptionLoading(true));
      const response = await instance.get(subscriptionConfig);
      const subscriptionsData: any = response.data;

      if (
        subscriptionsData &&
        typeof subscriptionsData === "object" &&
        subscriptionsData.razorpay &&
        subscriptionsData.stripe
      ) {
        dispatch(
          setSubscriptions({
            ...subscriptionsData,
          } as SubscriptionGroup),
        );
      } else {
        dispatch(
          setSubscriptionError(
            "Invalid subscription data structure received from server",
          ),
        );
      }
    } catch (error: any) {
      dispatch(
        setSubscriptionError(error.message || "Failed to load subscriptions"),
      );
    } finally {
      dispatch(setSubscriptionLoading(false));
    }
  };

/**
 * POST /subscription/create-checkout-session/ — plan upgrade / subscribe.
 * `returnPath` (Stripe only) tells the backend which frontend route to build
 * the success/cancel return URLs from; sent additively so it is harmless if
 * the backend ignores it.
 */
export const createSubscriptionCheckoutSession =
  (token: string, subscriptionPlanId: string | number, returnPath?: string) =>
  async (dispatch: Dispatch) => {
    const planId = String(subscriptionPlanId).trim();
    if (!planId) {
      dispatch(setSubscriptionError("Please select a subscription plan"));
      return null;
    }

    return redirectToStripeCheckout(dispatch, () =>
      instance.post(
        createCheckoutSession,
        {
          subscription_plan: planId,
          ...(returnPath ? { return_path: returnPath } : {}),
        },
        checkoutAuthHeaders(token),
      ),
    );
  };

interface TopupCheckoutResponse {
  order_id: string;
  amount: number;
  currency: string;
  credits?: number;
  session_url?: string;
  razorpay_key_id?: string;
}

/**
 * POST /subscription/topup/purchase/ — Create topup purchase order.
 * `returnPath` (Stripe only) tells the backend which frontend page to return
 * the user to after checkout. The Razorpay branch reads `data.order_id` and is
 * unaffected; the field is sent additively so it is harmless if ignored.
 */
export const createTopupCheckoutSession =
  (
    token: string,
    credits: number,
    currency: string = "INR",
    returnPath?: string,
  ) =>
  async (dispatch: Dispatch) => {
    const normalizedCredits = Number(credits);
    if (!Number.isFinite(normalizedCredits) || normalizedCredits <= 0) {
      dispatch(setSubscriptionError("Please enter a valid number of credits"));
      return null;
    }

    const normalizedCurrency =
      typeof currency === "string" && currency.trim().length > 0
        ? currency.trim().toUpperCase()
        : "INR";

    try {
      dispatch(setSubscriptionLoading(true));
      const response = await instance.post(
        topupPurchase,
        {
          credits: normalizedCredits,
          currency: normalizedCurrency,
          ...(returnPath ? { return_path: returnPath } : {}),
        },
        checkoutAuthHeaders(token),
      );

      const sessionUrl = response?.data?.session_url;
      if (sessionUrl && typeof sessionUrl === "string") {
        dispatch(setSubscriptionError(null));
        window.location.href = sessionUrl;
        return response.data;
      }

      const topupData: TopupCheckoutResponse = response?.data?.data;
      if (
        !topupData ||
        !topupData.order_id ||
        typeof topupData.amount !== "number" ||
        !topupData.currency
      ) {
        throw new Error("Invalid topup purchase response");
      }

      dispatch(setSubscriptionError(null));
      return {
        ...topupData,
        razorpay_key_id:
          (response?.data?.razorpay_key_id as string | undefined) ??
          topupData.razorpay_key_id,
      };
    } catch (error: any) {
      const message = extractApiErrorMessage(
        error,
        "Failed to create topup order",
      );
      dispatch(setSubscriptionError(message));
      // Re-throw so the consumer's try/catch can surface the message in a toast.
      throw new Error(message);
    } finally {
      dispatch(setSubscriptionLoading(false));
    }
  };

/** POST /subscription/razorpay/create-checkout-session/ — Get Razorpay subscription ID */
export const getRazorpaySubscriptionId =
  (token: string, amount: number, subscriptionPlanId: string | number) =>
  async (dispatch: Dispatch) => {
    const planId = String(subscriptionPlanId).trim();
    if (!planId) {
      dispatch(setSubscriptionError("Please select a subscription plan"));
      return null;
    }

    try {
      dispatch(setSubscriptionLoading(true));
      const response = await instance.post(
        razorpayCreateCheckoutSession,
        { amount: amount, currency: "INR", subscription_plan: Number(planId) },
        checkoutAuthHeaders(token),
      );

      const subscriptionId = response?.data?.data?.subscription_id;
      if (!subscriptionId) {
        throw new Error("Invalid subscription response");
      }

      dispatch(setSubscriptionError(null));
      return {
        subscriptionId,
        razorpay_key_id: response?.data?.razorpay_key_id as string | undefined,
      };
    } catch (error: any) {
      dispatch(
        setSubscriptionError(
          error.response?.data?.message ||
            error.message ||
            "Failed to create subscription",
        ),
      );
      return null;
    } finally {
      dispatch(setSubscriptionLoading(false));
    }
  };

/** POST /subscription/topup/purchase/ — Get order ID for topup */
export const getRazorpayTopupOrderId =
  (token: string, credits: number) => async (dispatch: Dispatch) => {
    return createTopupCheckoutSession(token, credits)(dispatch);
  };

/** POST /subscription/razorpay/create-checkout-session/ — Razorpay plan checkout (returns session_url) */
export const createRazorpayCheckoutSession =
  (token: string, amount: number, subscriptionPlanId: string | number) =>
  async (dispatch: Dispatch) => {
    const planId = String(subscriptionPlanId).trim();
    if (!planId) {
      dispatch(setSubscriptionError("Please select a subscription plan"));
      return null;
    }

    return redirectToStripeCheckout(dispatch, () =>
      instance.post(
        razorpayCreateCheckoutSession,
        { amount: amount, currency: "INR", subscription_plan: Number(planId) },
        checkoutAuthHeaders(token),
      ),
    );
  };

export const completeRazorpayOrder =
  (
    token: string,
    razorpay_subscription_id: string,
    razorpay_payment_id: string,
    razorpay_signature: string,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setSubscriptionLoading(true));
    try {
      const response = await instance.post(
        razorpayOrderComplete,
        {
          razorpay_subscription_id,
          razorpay_payment_id,
          razorpay_signature,
        },
        checkoutAuthHeaders(token),
      );
      dispatch(setSubscriptionError(null));
      return response.data;
    } catch (error: any) {
      const errorMsg = "Failed to complete order: " + error.message;
      dispatch(setSubscriptionError(errorMsg));
      return null;
    } finally {
      dispatch(setSubscriptionLoading(false));
    }
  };

export const fetchRzPaymentStatus =
  (token: string, subscriptionId: string) => async (dispatch: Dispatch) => {
    try {
      const response = await instance.get(
        `/subscription/check-rz-payment-status/${subscriptionId}/`,
        checkoutAuthHeaders(token),
      );

      return response.data;
    } catch (error: any) {
      console.error("Failed to check status:", error);
      dispatch(
        setSubscriptionError("Failed to check status: " + error.message),
      );
      return null;
    }
  };

export const cancelSubscriptions =
  (token: string, reason: string) => async (dispatch: Dispatch) => {
    dispatch(setSubscriptionLoading(true));
    try {
      await instance.post(
        `/subscription/cancel-subscription/`,
        { "cancel-subscription": "true", reason: reason },
        checkoutAuthHeaders(token),
      );
    } catch (error: any) {
      dispatch(setSubscriptionError("Failed to report job: " + error.message));
      dispatch(setSubscriptionLoading(false));
    } finally {
      dispatch(setSubscriptionLoading(false));
    }
  };

/** GET /subscription/subscription-details/ — active plan details */
export const fetchSubscriptionDetails =
  (token: string) => async (dispatch: Dispatch) => {
    try {
      const response = await instance.get(
        subscriptionDetailsRoute,
        checkoutAuthHeaders(token),
      );
      dispatch(setSubscriptionDetails(response.data));
      return response.data;
    } catch (error: any) {
      dispatch(
        setSubscriptionError(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch subscription details",
        ),
      );
      return null;
    }
  };
